import { FunctionComponent, ReactElement, ReactNode, useMemo } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import {
	LaunchOutlined as LaunchIcon,
	ContentCopyOutlined as ContentCopyIcon,
	PrintOutlined as Print,
} from '@mui/icons-material';
import { ToggleGroupNav } from '@/components/ui/tabs';
import { Button, ButtonLink } from '@/components/ui/buttons';
import { copyLink } from '@/shared/utils';
import { useCurrentTab } from '@/shared/hooks/use-current-tab';
import { DialogueContext } from '../dialogues.types';
import { DialoguesEmptyState } from '../components/empty-state';
import {
	TILE_SELECTION_TAB,
	AI_OUTPUT_TAB,
	TAB_ROUTES,
	IPS_TAB,
} from './risk-assessment.constants';
import {
	getClientPortalRiskAssessmentOutputUrl,
	getClientPortalRiskAssessmentTileSelectionUrl,
} from './risk-assessment.utils';
import { getToggleGroupRoute } from '../dialogues.utils';

const RiskAssessmentPage: FunctionComponent = (): ReactElement => {
	const { client, settings, activityLog } = useOutletContext<DialogueContext>();
	const { advisorId } = useParams();
	const routes = TAB_ROUTES.map((route) => (advisorId ? `/advisors/:advisorId${route}` : route));
	const currentTab = useCurrentTab(routes) || routes[0];

	const hasRiskAssessmentFlowStarted = useMemo(
		() => Boolean(activityLog.find((log) => log.flow === 'risk_assessment')),
		[activityLog]
	);
	const isRiskAssessmentFlowDisabled = useMemo(
		() => !settings.flows.find(({ flowName }) => flowName === 'risk_assessment'),
		[settings]
	);

	const clientPortalLinks = useMemo(
		() => ({
			[getToggleGroupRoute(advisorId, TILE_SELECTION_TAB.route)]:
				getClientPortalRiskAssessmentTileSelectionUrl(client),
			[getToggleGroupRoute(advisorId, AI_OUTPUT_TAB.route)]:
				getClientPortalRiskAssessmentOutputUrl(client),
		}),
		[advisorId, client]
	);
	const clientPortalLink = useMemo(
		() => clientPortalLinks[currentTab],
		[clientPortalLinks, currentTab]
	);

	const emptyContainer = (children: ReactNode) => (
		<Box py="3rem" px="2.5rem">
			{children}
		</Box>
	);

	const riskAssessmentNotStarted = emptyContainer(
		<DialoguesEmptyState
			title="Risk Assessment Questionnaire not started yet"
			text="Once the Risk Assessment Questionnaire exercise is initiated by the client, activities will populate in this section."
			callToAction={
				<ButtonLink
					variant="outlined"
					endIcon={<LaunchIcon />}
					to={clientPortalLink}
					target="_blank"
					rel="noopener noreferrer"
				>
					Open Risk Assessment
				</ButtonLink>
			}
		/>
	);

	const riskAssessmentDisabled = emptyContainer(
		<DialoguesEmptyState
			title="Risk Assessment Questionnaire is disabled"
			text="If you’d like to enable this section go back to Overview to change settings. "
		/>
	);

	const riskAssessmentOutlet = (
		<Box>
			<Stack direction="row" justifyContent="space-between" my="2rem">
				<ToggleGroupNav
					ariaLabel="Risk Assessment Questionnaire navigation"
					tabs={[TILE_SELECTION_TAB, AI_OUTPUT_TAB, IPS_TAB]}
				/>
				<Stack direction="row" gap="1rem">
					{currentTab === AI_OUTPUT_TAB.route && (
						<Button
							variant="outlined"
							endIcon={<Print />}
							onClick={() => window.print()}
							className="no-print"
						>
							Save as PDF
						</Button>
					)}
					<ButtonLink
						variant="outlined"
						endIcon={<LaunchIcon />}
						to={clientPortalLink}
						target="_blank"
						rel="noopener noreferrer"
					>
						Open Risk Assessment
					</ButtonLink>
					<Button
						variant="outlined"
						endIcon={<ContentCopyIcon />}
						onClick={() => copyLink(clientPortalLink)}
						className="no-print"
					>
						Copy Link
					</Button>
				</Stack>
			</Stack>
			<Outlet context={{ settings, client, activityLog } satisfies DialogueContext} />
		</Box>
	);

	const renderRiskAssessmentContent = (): ReactNode => {
		if (isRiskAssessmentFlowDisabled) return riskAssessmentDisabled;
		if (!hasRiskAssessmentFlowStarted) return riskAssessmentNotStarted;
		return riskAssessmentOutlet;
	};

	return <Stack direction="column">{renderRiskAssessmentContent()}</Stack>;
};

export default RiskAssessmentPage;
